import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    RtlsPlayerOptions,
    SiteRtlsPlayer,
} from '@myrtls/api-interfaces';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
    catchError,
    filter,
    map,
    switchMap,
    takeUntil,
    withLatestFrom,
} from 'rxjs/operators';
import {
    PLAYERS_ENDPOINT,
    PLAYERS_OPTIONS_ENDPOINT,
    SITES_API_URL,
} from '../../shared/utils/constants';
import { AppState } from '../index';
import { $selectedSiteId } from '../sites/sites.selectors';
import { interval, of } from 'rxjs';
import {
    getPlayerOptionsFailureAction,
    getPlayerOptionsRequestAction,
    getPlayerOptionsSuccessAction,
    getSitePlayerFailureAction,
    getSitePlayerRequestAction,
    getSitePlayerSuccessAction,
    initializeSitePlayerFailureAction,
    initializeSitePlayerRequestAction,
    initializeSitePlayerSuccessAction,
    startPoolingSitePlayerRequestAction,
    stopPoolingSitePlayerRequestAction,
    updateSitePlayerFailureAction,
    updateSitePlayerRequestAction,
    updateSitePlayerSuccessAction,
} from './player.actions';

@Injectable()
export class PlayerEffects {
    startPoolingSitePlayers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(startPoolingSitePlayerRequestAction),
            switchMap(({ intervalTime }) =>
                interval(intervalTime).pipe(
                    takeUntil(
                        this.actions$.pipe(
                            ofType(stopPoolingSitePlayerRequestAction),
                        ),
                    ),
                    map(getSitePlayerRequestAction),
                ),
            ),
        ),
    );

    getSitePlayer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getSitePlayerRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([, siteId]) => {
                return this.http
                    .get<SiteRtlsPlayer>(
                        `${SITES_API_URL}/${siteId}/${PLAYERS_ENDPOINT}`,
                    )
                    .pipe(
                        map(player =>
                            getSitePlayerSuccessAction({
                                player,
                            }),
                        ),
                        catchError(error =>
                            of(
                                getSitePlayerFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    );
            }),
        ),
    );

    getPlayerOptions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getPlayerOptionsRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([, siteId]) => {
                return this.http
                    .get<RtlsPlayerOptions>(
                        `${SITES_API_URL}/${siteId}/${PLAYERS_ENDPOINT}/${PLAYERS_OPTIONS_ENDPOINT}`,
                    )
                    .pipe(
                        map(options =>
                            getPlayerOptionsSuccessAction({
                                options,
                            }),
                        ),
                        catchError(error =>
                            of(
                                getPlayerOptionsFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    );
            }),
        ),
    );

    initializeSitePlayer = createEffect(() =>
        this.actions$.pipe(
            ofType(initializeSitePlayerRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ parameters }, siteId]) => {
                return this.http
                    .post<SiteRtlsPlayer>(
                        `${SITES_API_URL}/${siteId}/${PLAYERS_ENDPOINT}`,
                        parameters,
                    )
                    .pipe(
                        map(player =>
                            initializeSitePlayerSuccessAction({
                                player,
                            }),
                        ),
                        catchError(error =>
                            of(
                                initializeSitePlayerFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    );
            }),
        ),
    );

    updateSitePlayer = createEffect(() =>
        this.actions$.pipe(
            ofType(updateSitePlayerRequestAction),
            withLatestFrom(
                this.store.pipe(
                    select($selectedSiteId),
                    filter((id): id is string => id != null),
                ),
            ),
            switchMap(([{ parameters }, siteId]) => {
                return this.http
                    .put<SiteRtlsPlayer>(
                        `${SITES_API_URL}/${siteId}/${PLAYERS_ENDPOINT}`,
                        parameters,
                    )
                    .pipe(
                        map(player =>
                            updateSitePlayerSuccessAction({
                                player,
                            }),
                        ),
                        catchError(error =>
                            of(
                                updateSitePlayerFailureAction({
                                    error,
                                }),
                            ),
                        ),
                    );
            }),
        ),
    );

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store<AppState>,
        private readonly http: HttpClient,
    ) {}
}
